/**
 * /wireline-slickline/lines/:id — one line: its folded state, its event log,
 * and its details (GET / PATCH / DELETE /wireline-slickline/lines/:id, GET
 * /lines/:id/state, POST /lines/:id/events, POST /line-events/:id/void;
 * fieldiq-api#1336, WL-1).
 *
 * Panels stack full width: state, events, details, then the edit form when
 * editing. The state panel reads GET /lines/:id/state and the band reads the
 * same answer: nothing on this page folds the log itself.
 *
 * Who presses what (lib/wireline-slickline, mirroring the API's wl-roles):
 *   Log event, Edit line — WL_GEAR_WRITE (admin, office_staff, supervisor)
 *   Void event, Delete   — WL_ADMIN (admin, office_staff)
 * A line with events cannot be deleted (409 line_has_events): the API's
 * sentence is shown in the dialog.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, Cable, ClipboardCheck, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteWlLine, useUpdateWlLine, useWlLine, useWlLineState, useWlMeta } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminWl, canWriteWlGear, existingIdOf, wlCatalogLabel, wlDueSkin, wlLineLength, wlRefusal, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlLineEvent } from '../../types/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { LineForm } from './LineForm';
import { AddEventDialog, LineEventLog, VoidEventDialog } from './LineEvents';
import { LineStatePanel } from './LineStatePanel';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WL_SECONDARY, WlDialog, WlEmpty, WlFormError, WlLoading, WlPanel, WlSpec } from './WlBits';

export default function WlLineDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/lines');
  const { user } = useAuth();
  const query = useWlLine(id);
  const stateQuery = useWlLineState(id);
  const catalog = useWlMeta().data?.data ?? null;
  const canGear = canWriteWlGear(user?.role);
  const canAdmin = canAdminWl(user?.role);
  const update = useUpdateWlLine();
  const del = useDeleteWlLine();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<WlLineEvent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setAdding(false);
    setVoiding(null);
    setDeleting(false);
  }, [id]);

  const line = query.data?.data ?? null;
  const meta = query.data?.meta ?? stateQuery.data?.meta ?? null;
  const state = stateQuery.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const events = line?.events ?? [];
  // The current drum's NAME, read off the event that recorded that drum id. A lookup, not a fold.
  const drumName = state?.currentDrumEquipmentId
    ? events.find((e) => e.drumEquipmentId === state.currentDrumEquipmentId)?.drumName ?? null
    : null;
  const stateLoading = stateQuery.isLoading;
  const stateFailed = !!queryLoadFailure(stateQuery);

  return (
    <div data-testid="wl-line-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={line ? line.lineNumber || '—' : query.isLoading ? 'Line' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          line && (canGear || canAdmin) ? (
            <div className="flex flex-wrap gap-2">
              {canGear && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setAdding(true)}>
                  <Plus className="size-4" aria-hidden="true" />
                  Log event
                </button>
              )}
              {canGear && (
                <button
                  type="button"
                  className={WL_BAND_SECONDARY}
                  onClick={() => {
                    setEditError(null);
                    setDuplicateOf(null);
                    setEditing(true);
                  }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit line
                </button>
              )}
              {canAdmin && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Cable className="size-4" aria-hidden="true" />,
            label: 'On drum',
            tone: 'neutral',
            loading: stateLoading,
            value: stateFailed ? '—' : wlLineLength(state?.lengthOnDrum, state?.lengthUnit),
          },
          {
            icon: <ClipboardCheck className="size-4" aria-hidden="true" />,
            label: 'Pull test',
            tone: 'accent',
            loading: stateLoading,
            value: stateFailed || !state ? '—' : wlDueSkin(state.pullTest?.status).label,
          },
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Events',
            sub: 'Counted',
            tone: 'neutral',
            loading: stateLoading,
            value: stateFailed ? '—' : displayValue(state?.eventsCounted),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading line" />
          ) : notFound || (!failure && !line) ? (
            <WlPanel eyebrow="Line" title="Line">
              <WlEmpty>This line was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !line ? (
            <WlPanel eyebrow="Line" title="Line">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this line" />
            </WlPanel>
          ) : (
            <>
              <LineStatePanel query={stateQuery} meta={meta} drumName={drumName} />

              <LineEventLog events={events} catalog={catalog} meta={meta} canVoid={canAdmin} onVoid={setVoiding} />

              <WlPanel eyebrow="Line" title="Details" testId="wl-line-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Line number" value={line.lineNumber || '—'} mono />
                  <WlSpec label="Line type" value={line.lineTypeLabel ?? wlCatalogLabel(catalog?.lineTypes, line.lineType)} />
                  <WlSpec label="Manufacturer" value={line.manufacturer || '—'} />
                  <WlSpec label="Serial number" value={line.serialNumber || '—'} mono />
                  <WlSpec label="Material" value={line.material || '—'} />
                  <WlSpec label="OD" value={wlWithUnit(line.od, line.odUnit)} mono />
                  <WlSpec label="Breaking strength" value={wlWithUnit(line.breakingStrength, line.breakingStrengthUnit)} mono />
                  {line.conductorCount != null && <WlSpec label="Conductors" value={displayValue(line.conductorCount)} mono />}
                  <WlSpec label="Pull-test interval" value={wlWithUnit(line.pullTestIntervalDays, 'days')} mono />
                  <WlSpec label="Inspection interval" value={wlWithUnit(line.inspectionIntervalDays, 'days')} mono />
                </dl>
                {line.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{line.notes}</p>}
              </WlPanel>

              {editing && canGear && (
                <WlPanel eyebrow="Edit" title="Edit line" testId="wl-line-edit">
                  <LineForm
                    key={line.id}
                    line={line}
                    catalog={catalog}
                    meta={meta}
                    pending={update.isPending}
                    error={editError}
                    duplicateOf={duplicateOf}
                    submitLabel="Save line"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      setDuplicateOf(null);
                      update.mutate(
                        { id: line.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Line saved');
                            setEditing(false);
                          },
                          onError: (err) => {
                            setEditError(wlRefusal(err, 'The line could not be saved.'));
                            setDuplicateOf(existingIdOf(err, 'existingLineId'));
                          },
                        },
                      );
                    }}
                  />
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>

      {line && adding && canGear && <AddEventDialog lineId={line.id} catalog={catalog} meta={meta} onClose={() => setAdding(false)} />}
      {voiding && canAdmin && <VoidEventDialog event={voiding} catalog={catalog} onClose={() => setVoiding(null)} />}

      {line && canAdmin && (
        <WlDialog
          open={deleting}
          title="Delete line"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep line
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(line.id, {
                    onSuccess: () => {
                      toast.success('Line deleted');
                      navigate('/wireline-slickline/lines', { replace: true });
                    },
                    onError: (err) => setDeleteError(wlRefusal(err, 'The line could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete line'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {line.lineNumber} from the register. A line with logged events cannot be deleted: log a Retired event instead.
          </p>
        </WlDialog>
      )}
    </div>
  );
}
